/**
 * Google Analytics 4 loader — gracefully no-ops when REACT_APP_GA_ID is empty.
 * Provides usePageTracking() to fire pageview on route changes.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const GA_ID = process.env.REACT_APP_GA_ID;

let loaded = false;
function loadGA() {
  if (loaded || !GA_ID || typeof window === "undefined") return;
  loaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_ID, { send_page_view: false });
}

export function usePageTracking() {
  const location = useLocation();
  useEffect(() => {
    if (!GA_ID) return;
    loadGA();
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search]);
}

export const isAnalyticsEnabled = () => !!GA_ID;

// GTM conversion events — safe no-throw push (GTM snippet owns dataLayer).
export function pushEvent(payload) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch { /* never block UI on analytics */ }
}

// Jun 2026 — GTM form-conversion events. Consistent payload shape:
// { event, form_name, form_type, page_path, submission_status } plus
// optional non-PII context (booking_type, test_type, campaign_source,
// registration_type…). Fire ONLY after the backend confirms success.
export function pushFormEvent(event, context = {}) {
  const page_path = typeof window !== "undefined" ? window.location.pathname : null;
  pushEvent({
    event,
    page_path,
    submission_status: "success",
    ...context,
  });
  // Sep 2026 — generic GTM conversion trigger. Fires ONLY here (i.e. on a
  // confirmed successful form submission), never on page load.
  pushEvent({
    event: "form_submission",
    form_type: event,
    page_path,
    ...context,
  });
}
